import React, { useEffect, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Add, Settings, Visibility } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import GeneralLoading from "../../../shared-components/Loading";
import StatBox from "../../../shared-components/StatBox";
import { FruitifyAssignmentSearch } from "../../assignment/components/FruitifyAssignmentSearch";
import {
  formatWeekDifference,
  getWeekDiffColor,
} from "../../../utils/formatters";
import { withPageTracking } from "../../../services/postHog";
import { FruitifyQuizSearch } from "../../quiz/components/FruitifyQuizSearch";
import { AssignmentsOrQuizzesToggle } from "../../../shared-components/AssignmentsOrQuizzesToggle";
import { AssistantInlineCard } from "../../assistant/AssistantInlineCard";
import ClassViewAdmin from "../../administrator/routes/ClassViewAdmin";
import { checkHasApprovedSubscription } from "../../auth/Utils";
import Tooltip from "@mui/material/Tooltip";

const ClassViewSummary = ({
  schoolClass,
  allowSwitch,
  adminView,
  setAdminView,
}) => {
  const navigate = useNavigate();
  return (
    <Card data-testid={`class-summary-${schoolClass.id}`}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">{schoolClass.name}</Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() =>
                navigate(`/assignments/create?classId=${schoolClass.id}`)
              }
            >
              Create Assignment
            </Button>
            {allowSwitch && (
              <Tooltip
                title={
                  adminView ? "Back to class view" : "Manage class and students"
                }
              >
                <IconButton
                  onClick={() => setAdminView(!adminView)}
                  sx={{ border: 0 }}
                  aria-label={
                    adminView
                      ? "Back to class view"
                      : "Manage class and students"
                  }
                >
                  {adminView ? <Visibility /> : <Settings />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={4}>
            <InformationSnippet
              title={"Subject"}
              value={schoolClass.subject.name}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Academic Year"}
              value={schoolClass.academicYear.name}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Year"}
              value={`Year ${schoolClass.yearGroup.yearGroup}`}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

function ClassViewBase() {
  const [adminView, setAdminView] = useState(false);
  const hasApprovedSubscription = checkHasApprovedSubscription();
  if (!adminView) {
    return (
      <ClassViewTeacher
        allowSwitch={!hasApprovedSubscription}
        adminView={adminView}
        setAdminView={setAdminView}
      />
    );
  } else {
    return (
      <ClassViewAdmin
        allowSwitch={!hasApprovedSubscription}
        adminView={adminView}
        setAdminView={setAdminView}
      />
    );
  }
}

function ClassViewTeacher({ allowSwitch, adminView, setAdminView }) {
  const { id } = useParams();
  const ApiClient = useApiClient();

  const [schoolClass, setSchoolClass] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [assignmentsOrQuizzes, setAssignmentsOrQuizzes] =
    useState("Assignments");

  useEffect(() => {
    fetchSchoolClass();
  }, [id]);

  const fetchSchoolClass = async () => {
    setLoading(true);
    setError(false);

    try {
      const response = await ApiClient.schools.getSchoolClassData({
        schoolClass: id,
      });

      if (response?.schoolClass) {
        setSchoolClass(response.schoolClass);
      } else {
        setSchoolClass(null);
        setError(true);
      }
    } catch (error) {
      console.error("Failed to fetch school class", error);

      setSchoolClass(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (error || !schoolClass) {
    return (
      <FruitifyContainer>
        <Card>
          <CardContent>
            <Typography variant="h5">Unable to load class</Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The class could not be loaded. Please try again.
            </Typography>
          </CardContent>
        </Card>
      </FruitifyContainer>
    );
  }

  return (
    <FruitifyContainer>
      <ClassViewSummary
        schoolClass={schoolClass}
        allowSwitch={allowSwitch}
        adminView={adminView}
        setAdminView={setAdminView}
      />

      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          mb: 2,
        }}
      >
        <Grid size={6}>
          <StatBox
            title="Assignments Created"
            stat={schoolClass.stats.assignmentsCreatedStats.current}
            footer={formatWeekDifference(
              schoolClass.stats.assignmentsCreatedStats,
            )}
            footerColor={getWeekDiffColor(
              schoolClass.stats.assignmentsCreatedStats,
            )}
          />
        </Grid>

        <Grid size={6}>
          <StatBox
            title="Answers Marked"
            stat={schoolClass.stats.answersMarkedStats.current}
            footer={formatWeekDifference(schoolClass.stats.answersMarkedStats)}
            footerColor={getWeekDiffColor(schoolClass.stats.answersMarkedStats)}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          mb: 2,
        }}
      >
        <Grid size={12}>
          <AssistantInlineCard
            context={{
              class_id: schoolClass.id,
            }}
            title={`Bud – ${schoolClass.name}`}
          />
        </Grid>
      </Grid>

      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          width: "100%",
        }}
      >
        <AssignmentsOrQuizzesToggle
          assignmentsOrQuizzes={assignmentsOrQuizzes}
          setAssignmentsOrQuizzes={setAssignmentsOrQuizzes}
        />
      </Stack>

      {assignmentsOrQuizzes === "Assignments" ? (
        <FruitifyAssignmentSearch forSchoolClass={schoolClass} excludeTitle />
      ) : (
        <FruitifyQuizSearch forSchoolClass={schoolClass} excludeTitle />
      )}
    </FruitifyContainer>
  );
}
export { ClassViewSummary };
export default withPageTracking(ClassViewBase, { feature: "class-view" });
